import ArrowUpIcon from "@assets/icons/new/arrow-up_10px.svg?react";
import { SphereIconButton } from "@components/common/sphere-icon-button";
import { DefaultTableSizeOptions } from "@components/common/faro-table/faro-table-types";
import { MemberGroupProjects } from "@components/common/member/member-group-projects";
import {
  ACTION_BTN_SIZE,
  ACTION_ICON_SIZE,
} from "@components/common/project-actions";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { GroupId } from "@custom-types/base-types";
import { BaseMemberProps } from "@custom-types/member-types";
import { ArrowDownIcon, neutral } from "@flat-ui";
import { useDateTime } from "@hooks/use-date-time";
import { useMediaQueryList } from "@hooks/use-media-query";
import { useTableColumnWidth } from "@hooks/use-table-column-width";
import { Box, SxProps } from "@mui/material";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { withEllipsis } from "@styles/common-styles";
import { formatUserRoleType } from "@utils/data-display";
import { UserProfile } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { PropsWithChildren, useState } from "react";

interface MemberGroupsTableProps extends BaseMemberProps {
  /** Company member groups */
  groups: FaroCoreApiTypes.GroupSummaryResponse[];
}

/** Renders a table with the groups of a company member */
export function MemberGroupsTable({
  groups,
  member,
}: MemberGroupsTableProps): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const [expandedGroups, setExpandedGroups] = useState<GroupId[]>([]);
  const currentUser = useAppSelector(currentUserSelector);

  return (
    <Box component="div">
      {groups.map((group) => (
        <Box component={"div"} key={group.id}>
          <MemberGroupRow
            group={group}
            expandedGroups={expandedGroups}
            setExpandedRows={(groups) => {
              trackEvent({
                name: UserProfile.expandGroupProjects,
                props: {
                  // Stores whether the action is done for displaying the current user
                  isCurrentUser: currentUser?.identity === member?.identity,
                },
              });
              setExpandedGroups(groups);
            }}
          />
          {expandedGroups.includes(group.id) && (
            <MemberGroupProjects group={group} />
          )}
        </Box>
      ))}
    </Box>
  );
}

interface MemberGroupRowProps {
  /** Group element*/
  group: FaroCoreApiTypes.GroupSummaryResponse;

  /** Array of IDs of the expanded group */
  expandedGroups: GroupId[];

  /** Callback to set the expanded groups */
  setExpandedRows: React.Dispatch<React.SetStateAction<GroupId[]>>;
}

/** Renders a single group row */
function MemberGroupRow({
  group,
  expandedGroups,
  setExpandedRows,
}: MemberGroupRowProps): JSX.Element {
  const { isScreenMdOrLarger } = useMediaQueryList();
  const { xsAndSmColumnsWidth, mdAndLargerColumnsWidth } =
    useTableColumnWidth();

  const { formatDate } = useDateTime();

  const isGroupExpanded = expandedGroups.includes(group.id);

  const options = {
    isScreenMdOrLarger,
    xsAndSmColumnsWidth,
    mdAndLargerColumnsWidth,
  };

  function handleExpandGroup(event: React.MouseEvent<HTMLElement>): void {
    event.preventDefault();
    const updatedExpandedGroups = [...expandedGroups];

    // Get the index in the expandedGroups array for clicked group
    const expandedGroupIndex = expandedGroups.indexOf(group.id);

    if (expandedGroupIndex === -1) {
      // If the index is not found it means that the clicked group is collapsed
      // Add the clicked group to expandedGroups array
      updatedExpandedGroups.push(group.id);
    } else {
      // If the index is found it means that the clicked group is expanded
      // Remove the clicked group from expandedGroups array
      updatedExpandedGroups.splice(expandedGroupIndex, 1);
    }

    setExpandedRows(updatedExpandedGroups);
  }

  return (
    <Box
      component="div"
      sx={{
        display: "flex",
        flexFlow: isScreenMdOrLarger ? "row" : "column",
        justifyContent: isScreenMdOrLarger ? "space-between" : "normal",
        backgroundColor: neutral[50],
        "&:hover": {
          backgroundColor: neutral[100],
          cursor: "pointer",
        },
        mb: "4px",
      }}
      onClick={handleExpandGroup}
    >
      {/* Name */}
      <MemberGroupCell
        options={options}
        sx={{
          fontSize: "14px",
          fontWeight: "bold",
        }}
      >
        <var>{group.name}</var>
      </MemberGroupCell>

      {/* Creation date */}
      <MemberGroupCell options={options}>
        <var>{formatDate(group.createdAt)}</var>
      </MemberGroupCell>

      {/* Role */}
      <MemberGroupCell options={options}>
        {group.role ? formatUserRoleType(group.role) : "Project Access"}
      </MemberGroupCell>

      {/* Expand/collapse button */}
      <MemberGroupCell
        options={options}
        sx={{
          minWidth: "30px",
          maxWidth: "62px",
        }}
      >
        <SphereTooltip
          title={
            isGroupExpanded ? "Hide group projects" : "Show group projects"
          }
        >
          <SphereIconButton
            buttonSize={ACTION_BTN_SIZE}
            iconSize={ACTION_ICON_SIZE}
            component={isGroupExpanded ? ArrowUpIcon : ArrowDownIcon}
            onClick={handleExpandGroup}
          />
        </SphereTooltip>
      </MemberGroupCell>
    </Box>
  );
}

interface MemberGroupCellProps extends DefaultTableSizeOptions {
  /** Optional style properties  */
  sx?: SxProps;
}

/** Renders a row cell */
function MemberGroupCell({
  children,
  options,
  sx,
}: PropsWithChildren<MemberGroupCellProps>): JSX.Element {
  return (
    <Box
      component="div"
      sx={{
        display: "flex",
        alignItems: "center",
        height: options.isScreenMdOrLarger ? "70px" : "35px",
        minWidth: "70px",
        width: options.isScreenMdOrLarger
          ? "100%"
          : options.xsAndSmColumnsWidth,
        ...withEllipsis,
        padding: "8px 16px",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}
